import React, { useEffect, useRef, useState } from "react";

// On-page filters for list screens: one search box, a few dropdowns and an
// optional date range, with the active filters as removable chips. Changes
// apply straight away; typing is debounced. Replaces the pop-up filters where
// each field had its own tab and had to be cleared before using another.
//
// Render it OUTSIDE the area that swaps to a loader, so typing keeps focus.
//
// props:
//   values     current filter object (owned by the page)
//   onChange   (nextValues) => void — the page fetches page 1 with them
//   search     { key, placeholder }                      optional
//   selects    [{ key, allLabel, chipLabel, options: [{ value, label }],
//                 toValue?(stored) → value, fromOption?(option|null) → stored }]
//              By default the stored value is the option's value ("" = all).
//   dateRange  { fromKey, toKey, label }                  optional, values yyyy-mm-dd
//   clearValues  object merged in by "Clear all" (defaults: everything emptied)

const SEARCH_DELAY_MS = 400;

const fmt = (d) => (d ? d.split("-").reverse().join("-") : "");

const controlClass =
  "h-[38px] rounded-lg border border-gray-300 px-2 text-sm bg-white focus:outline-none focus:border-[#FF702B]";

const FilterBar = ({ values, onChange, search, selects = [], dateRange, clearValues }) => {
  const searchKey = search?.key;
  const [text, setText] = useState(searchKey ? values[searchKey] || "" : "");
  const timer = useRef(null);
  const inputRef = useRef(null);

  // Keep the box in step when filters change from outside (e.g. a reset)
  useEffect(() => {
    if (!searchKey) return;
    if ((values[searchKey] || "") !== text && !timer.current) setText(values[searchKey] || "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchKey ? values[searchKey] : null]);

  useEffect(() => () => clearTimeout(timer.current), []);

  const set = (changes) => onChange({ ...values, ...changes });

  const onType = (value) => {
    setText(value);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      timer.current = null;
      set({ [searchKey]: value.trim() });
    }, SEARCH_DELAY_MS);
  };

  const clearSearch = () => {
    clearTimeout(timer.current);
    timer.current = null;
    setText("");
    set({ [searchKey]: "" });
    inputRef.current?.focus();
  };

  const selectValue = (s) => (s.toValue ? s.toValue(values[s.key]) : values[s.key]) || "";
  const storeOption = (s, option) => (s.fromOption ? s.fromOption(option) : option ? option.value : "");

  const chips = [
    searchKey && values[searchKey] && { key: searchKey, label: `“${values[searchKey]}”`, clear: clearSearch },
    ...selects.map((s) => {
      const v = selectValue(s);
      if (!v) return null;
      const opt = s.options.find((o) => String(o.value) === String(v));
      return {
        key: s.key,
        label: `${s.chipLabel}: ${opt ? opt.label : v}`,
        clear: () => set({ [s.key]: storeOption(s, null) }),
      };
    }),
    dateRange &&
      (values[dateRange.fromKey] || values[dateRange.toKey]) && {
        key: "date",
        label: `${dateRange.label}: ${fmt(values[dateRange.fromKey]) || "…"} – ${fmt(values[dateRange.toKey]) || "…"}`,
        clear: () => set({ [dateRange.fromKey]: "", [dateRange.toKey]: "" }),
      },
  ].filter(Boolean);

  const clearAll = () => {
    clearTimeout(timer.current);
    timer.current = null;
    setText("");
    const cleared = {};
    if (searchKey) cleared[searchKey] = "";
    selects.forEach((s) => (cleared[s.key] = storeOption(s, null)));
    if (dateRange) {
      cleared[dateRange.fromKey] = "";
      cleared[dateRange.toKey] = "";
    }
    onChange({ ...values, ...cleared, ...(clearValues || {}) });
  };

  return (
    <div className="bg-[#FFFFFF] shadow-custom rounded-[14px] px-[12px] py-[10px] mb-3">
      <div className="flex flex-wrap items-center gap-2">
        {searchKey && (
          <div className="relative flex-1" style={{ minWidth: "240px" }}>
            <i className="fa fa-search absolute left-3 top-[11px] text-gray-400 text-sm"></i>
            <input
              ref={inputRef}
              type="text"
              role="searchbox"
              aria-label="Search"
              value={text}
              onChange={(e) => onType(e.target.value)}
              onKeyDown={(e) => {
                // Esc clears the text first; only an empty box lets Esc through
                // (e.g. to close the pop-up the bar sits in)
                if (e.key === "Escape" && text) {
                  e.stopPropagation();
                  clearSearch();
                }
              }}
              placeholder={search.placeholder}
              className="w-full h-[38px] rounded-lg border border-gray-300 pl-9 pr-8 text-sm focus:outline-none focus:border-[#FF702B]"
            />
            {text && (
              <button
                type="button"
                aria-label="Clear search"
                onClick={clearSearch}
                className="absolute right-2 top-[7px] text-gray-400 hover:text-gray-600 text-lg leading-none"
              >
                ×
              </button>
            )}
          </div>
        )}

        {selects.map((s) => (
          <select
            key={s.key}
            aria-label={s.chipLabel}
            className={controlClass}
            value={selectValue(s)}
            onChange={(e) =>
              set({ [s.key]: storeOption(s, s.options.find((o) => String(o.value) === e.target.value) || null) })
            }
          >
            <option value="">{s.allLabel}</option>
            {s.options.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        ))}

        {dateRange && (
          <div className="flex items-center gap-1 text-sm text-gray-500">
            <span>{dateRange.label}</span>
            <input
              type="date"
              aria-label={`${dateRange.label} from`}
              className={controlClass}
              value={values[dateRange.fromKey] || ""}
              max={values[dateRange.toKey] || undefined}
              onChange={(e) => set({ [dateRange.fromKey]: e.target.value })}
            />
            <span>to</span>
            <input
              type="date"
              aria-label={`${dateRange.label} to`}
              className={controlClass}
              value={values[dateRange.toKey] || ""}
              min={values[dateRange.fromKey] || undefined}
              onChange={(e) => set({ [dateRange.toKey]: e.target.value })}
            />
          </div>
        )}
      </div>

      {chips.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 mt-2">
          {chips.map((c) => (
            <span
              key={c.key}
              className="inline-flex items-center gap-1 rounded-full bg-orange-50 text-[#c2410c] border border-orange-200 px-3 py-[2px] text-xs"
            >
              {c.label}
              <button type="button" aria-label={`Remove ${c.label}`} onClick={c.clear} className="ml-1 leading-none">
                ×
              </button>
            </span>
          ))}
          {chips.length > 1 && (
            <button type="button" onClick={clearAll} className="text-xs text-gray-500 underline">
              Clear all
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default FilterBar;
